import { useState } from 'react';

interface AirlineLogoProps {
  code: string;
  size?: number;
  className?: string;
  showFallback?: boolean;
}

// Airline code to name mapping for alt text and fallback
const airlineNames: Record<string, string> = {
  'AA': 'American Airlines',
  'DL': 'Delta Air Lines',
  'UA': 'United Airlines',
  'WN': 'Southwest Airlines',
  'B6': 'JetBlue Airways',
  'AS': 'Alaska Airlines',
  'NK': 'Spirit Airlines',
  'F9': 'Frontier Airlines',
  'G4': 'Allegiant Air',
  'SY': 'Sun Country Airlines',
  'HA': 'Hawaiian Airlines',
  // International
  'BA': 'British Airways',
  'LH': 'Lufthansa',
  'AF': 'Air France',
  'KL': 'KLM',
  'EK': 'Emirates',
  'QR': 'Qatar Airways',
  'SQ': 'Singapore Airlines',
  'CX': 'Cathay Pacific',
  'JL': 'Japan Airlines',
  'NH': 'All Nippon Airways',
  'QF': 'Qantas',
  'AC': 'Air Canada',
  'AM': 'Aeromexico',
  'AV': 'Avianca',
  'LA': 'LATAM',
  'IB': 'Iberia',
  'TK': 'Turkish Airlines',
  'EY': 'Etihad Airways',
  'VS': 'Virgin Atlantic',
};

// Brand colors for fallback badges
const airlineColors: Record<string, { bg: string; text: string }> = {
  'AA': { bg: '#0078D2', text: 'white' },
  'DL': { bg: '#003366', text: 'white' },
  'UA': { bg: '#002244', text: 'white' },
  'WN': { bg: '#304CB2', text: '#FFBF27' },
  'B6': { bg: '#003876', text: 'white' },
  'AS': { bg: '#01426A', text: 'white' },
  'NK': { bg: '#FFE600', text: 'black' },
  'F9': { bg: '#00A651', text: 'white' },
  'BA': { bg: '#075AAA', text: 'white' },
  'LH': { bg: '#05164D', text: '#FFC72C' },
  'AF': { bg: '#002157', text: 'white' },
  'EK': { bg: '#D71921', text: 'white' },
  'QR': { bg: '#5C0632', text: 'white' },
  'SQ': { bg: '#F7BE00', text: '#1A3668' },
};

/**
 * Loads airline logos from airhex.com with a branded badge fallback.
 * Shows the fallback immediately while the image loads (opacity trick),
 * then fades the image in on load. On error, permanently shows the
 * badge so there's always a recognizable visual.
 */
export function AirlineLogo({ code, size = 24, className = '', showFallback = true }: AirlineLogoProps) {
  const [imageError, setImageError] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);

  const upperCode = code.toUpperCase();
  const airlineName = airlineNames[upperCode] || upperCode;
  const colors = airlineColors[upperCode] || { bg: '#44403C', text: 'white' };

  const logoUrl = `/airlines/${upperCode}.png`;

  if (imageError || !showFallback) {
    // Fallback: styled badge with airline code
    return (
      <div
        className={`inline-flex items-center justify-center rounded font-serif font-bold ${className}`}
        style={{
          width: size,
          height: size,
          backgroundColor: colors.bg,
          color: colors.text,
          fontSize: size * 0.4,
        }}
        title={airlineName}
      >
        {upperCode}
      </div>
    );
  }

  return (
    <div
      className={`relative inline-flex items-center justify-center overflow-hidden rounded-lg ${className}`}
      style={{ width: size, height: size }}
      title={airlineName}
    >
      {/* Loading/fallback background */}
      {!imageLoaded && (
        <div
          className="absolute inset-0 rounded flex items-center justify-center font-serif font-bold"
          style={{
            backgroundColor: colors.bg,
            color: colors.text,
            fontSize: size * 0.4,
          }}
        >
          {upperCode}
        </div>
      )}

      {/* Logo image */}
      <img
        src={logoUrl}
        alt={airlineName}
        className={`object-cover transition-opacity duration-200 ${imageLoaded ? 'opacity-100' : 'opacity-0'}`}
        style={{ width: size, height: size }}
        onLoad={() => setImageLoaded(true)}
        onError={() => setImageError(true)}
        loading="lazy"
      />
    </div>
  );
}

// Compact version for inline use (just the logo, smaller)
export function AirlineLogoInline({ code, size = 20 }: { code: string; size?: number }) {
  return <AirlineLogo code={code} size={size} className="shrink-0" />;
}
